import type { Envelope, Suit, TableEvent, TableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { hokmPlayable } from "../game/cards";
import { showCarpet } from "../game/carpet";
import { Sequencer } from "../scene/queue";
import { introGate } from "../game/stake";
import { buildProof, cardCode, DealWatch, type DealProof } from "../game/dealLock";
import { BreakWatch, RESULT_EVENTS } from "../game/handBreak";
import { botText } from "../game/seats";

import { applyState, cutKindOf, emptyHokm, toLocal, type HokmLocal } from "./state";
import type { HokmScene } from "./scene";

/**
 * کنترلرِ میزِ آنلاینِ حکم — پورتِ HokmOnlineController.
 *
 * قاعده‌ها همان‌اند:
 *   • هیچ قاعده‌ای این‌جا اجرا نمی‌شود؛ حقیقت TableStateِ سرور است.
 *   • رویدادها و وضعیت‌ها **به ترتیبِ رسیدن** در یک صف اجرا می‌شوند، پس انیمیشنِ
 *     کارت پیش از نشستنِ وضعیتِ تازه تمام می‌شود.
 *   • لمسِ بازیکن به پیام تبدیل می‌شود و تا پاسخِ سرور قفل می‌ماند.
 *
 * یک فرق با نسخه‌ی اپ، عمدی: اپ رویدادِ ace_draw را نادیده می‌گیرد و مراسمِ
 * آس‌کشی فقط در میزِ آفلاینش هست. سرور این رویداد را برای همین نمایش می‌فرستد،
 * پس این‌جا همان AnimStarterDrawِ خودِ HokmView را اجرا می‌کنیم.
 */
export type Overlay =
  | { kind: "hand"; unit: number; points: number; kot: boolean; hakemKot: boolean }
  | { kind: "match"; unit: number };

export class HokmOnline {
  private seq = new Sequencer();
  private scene: HokmScene;
  private off: (() => void) | null = null;
  private gotState = false;
  private seatsLaid = false;
  private quitting = false;
  private ceremonyDone = false;
  private startShown = false;
  private st: HokmLocal = emptyHokm();

  readonly awaitingHumanPlay = signal(false);
  readonly awaitingTrump = signal(false);
  readonly overlay = signal<Overlay | null>(null);
  /** مکثِ میانِ دو دست — «ادامه» ِ همه ([[BreakWatch]]). */
  readonly brk = new BreakWatch();
  /** قفلِ پخشِ دستی که تمام شد — [[DealProof]]؛ null = نشانی نیست. */
  readonly proof = signal<DealProof | null>(null);
  private deal = new DealWatch();
  /** مهلتِ نوبت: باقی‌مانده و کلِ این نوبت، ثانیه. null = بی‌مهلت. */
  readonly timer = signal<{ left: number; total: number } | null>(null);
  private turnEndsAt = 0; private turnTotal = 0; private tick: ReturnType<typeof setInterval> | null = null;

  constructor(scene: HokmScene) { this.scene = scene; }

  get state(): HokmLocal { return this.st; }

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    // پرده‌ی شرط بالاست؟ صحنه پیام‌ها را از همین حالا می‌گیرد، ولی تا
    // بالا رفتنِ پرده چیزی نمی‌کشد — وگرنه آس‌کشی و پخشِ ورق پشتِ پرده
    // انجام می‌شد و بازیکن میزی را می‌دید که ورق‌ها در دستش سبز شده‌اند.
    this.seq.hold(introGate());
    // میز ممکن است پیش از ساختِ صحنه شروع شده باشد (مهمانِ اتاق).
    const ace = client.aceDraw.value;
    if (ace) this.seq.enqueue(() => this.playEvent(ace));
    const last = client.table.value;
    if (last) this.seq.enqueueSnapshot(() => this.applyState(last));
    this.tick = setInterval(() => this.updateTimer(), 200);
    // دستگیره‌ی اشکال‌زدایی در کنسول — مثلِ «App.Debug» در اپ
    (window as unknown as { __hokm?: HokmOnline }).__hokm = this;
  }

  /** برای اشکال‌زدایی از کنسول. */
  get debug() { return { turnEndsAt: this.turnEndsAt, turnTotal: this.turnTotal, st: this.st }; }

  dispose(): void {
    this.quitting = true;
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
  }

  // ---------------------------------------------------------------- ورودیِ بازیکن

  clickCard(i: number): void {
    if (this.quitting || !this.awaitingHumanPlay.value) return;
    const card = this.st.myHand[i];
    if (!card) return;
    const lead = this.st.trickLeader >= 0 ? this.st.ledSuit : undefined;
    if (!hokmPlayable(this.st.myHand, lead)(card)) { this.scene.toast("باید هم‌خال بدهی"); return; }
    this.awaitingHumanPlay.value = false;     // تا پاسخِ سرور، دوباره‌فرستادن را ببند
    this.scene.markPlayable([]);
    this.timer.value = null; this.turnEndsAt = 0;
    client.playCard(card);
  }

  pickTrump(suit: Suit): void {
    if (this.quitting || !this.awaitingTrump.value) return;
    this.awaitingTrump.value = false;
    this.timer.value = null; this.turnEndsAt = 0;
    client.callTrump(suit);
  }

  /** «ادامه»: به سرور می‌گوید آماده‌ام و پنل تا پخشِ دستِ بعد باز می‌ماند؛ سرورِ کهنه فقط پنل را می‌بندد. */
  nextHand(): void { if (!this.brk.press()) this.overlay.value = null; }

  // ---------------------------------------------------------------- پیام‌های سرور

  private onMessage(env: Envelope): void {
    if (this.quitting) return;
    switch (env.msg.case) {
      case "tableState": { const ts = env.msg.value; this.seq.enqueueSnapshot(() => this.applyState(ts), this.brk.mustShow(ts.brk)); break; }
      case "tableEvent": { const ev = env.msg.value; this.seq.enqueue(() => this.playEvent(ev), RESULT_EVENTS.has(ev.kind)); break; }
      case "error": this.showError(env.msg.value.code); break;
    }
  }

  private showError(code: string): void {
    const msg: Record<string, string> = {
      not_your_turn: "نوبت تو نیست", follow_suit: "باید هم‌خال بدهی",
      not_in_hand: "این کارت در دستت نیست", not_hakem: "حکم را حاکم اعلام می‌کند",
    };
    if (!msg[code]) return;
    this.scene.toast(msg[code]!);
    this.awaitingHumanPlay.value = this.st.myTurn;   // قفلِ خوش‌بینانه را باز کن
    this.refreshPlayable();
  }

  private refreshPlayable(): void {
    if (!this.awaitingHumanPlay.value) { this.scene.markPlayable([]); return; }
    const lead = this.st.trickLeader >= 0 ? this.st.ledSuit : undefined;
    const ok = hokmPlayable(this.st.myHand, lead);
    this.scene.markPlayable(this.st.myHand.map(ok));
  }

  private async applyState(ts: TableState): Promise<void> {
    // پنلِ نتیجه دستِ کم PANEL_MIN_MS دیده شود، حتی اگر این صفحه پشتِ سرور مانده — [[BreakWatch.keep]].
    await this.brk.keep(this.seq, ts.brk, this.overlay.value?.kind === "hand");
    if (this.quitting) return;
    const first = !this.gotState;
    this.st = applyState(ts, this.st);
    this.gotState = true;
    this.brk.update(ts.brk, ts.seats, ts.me, ts.watching);
    if (!ts.watching) this.deal.observe(ts.dealLock, ts.myHand.map((c) => cardCode(c)));

    // پرچم‌های نوبت **پیش از** رندر — نما موقعِ کشیدنِ دست از همین‌ها می‌پرسد.
    this.awaitingHumanPlay.value = this.st.myTurn;
    this.awaitingTrump.value = this.st.awaitingTrumpFromMe;

    if (first) showCarpet(this.st.carpet);
    if (!this.seatsLaid || first) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }
    this.scene.render(this.st);
    this.scene.renderTrick(this.st);
    this.scene.updateScores(this.st);
    this.scene.setTurn(this.st);
    this.refreshPlayable();

    // بی‌قید و شرط، از روی خودِ وضعیت — [[HokmScene.syncTrump]]. حکمی که پیش
    // از رسیدنِ ما خوانده شده (تماشاچی، یا بازگشت از قطعی) مراسمش گذشته ولی
    // خودش باید دیده شود، و حکمی که عوض شده باید عوض شود.
    this.scene.syncTrump(this.st.trumpChosen, this.st.trump);
    if (this.overlay.value && !this.st.handOver && !this.st.matchOver) this.overlay.value = null;

    if ((this.st.myTurn || this.st.awaitingTrumpFromMe) && this.st.turnRemaining > 0.1) {
      this.turnTotal = Math.max(this.turnTotal, this.st.turnRemaining);
      this.turnEndsAt = performance.now() + this.st.turnRemaining * 1000;
    } else { this.turnEndsAt = 0; this.turnTotal = 0; this.timer.value = null; }
  }

  private updateTimer(): void {
    if (this.turnEndsAt <= 0) { if (this.timer.value) { this.timer.value = null; this.scene.setTimer(0, 0); } return; }
    const left = (this.turnEndsAt - performance.now()) / 1000;
    if (left <= 0) { this.turnEndsAt = 0; this.timer.value = null; this.scene.setTimer(0, 0); return; }
    const total = Math.max(left, this.turnTotal);
    this.timer.value = { left, total };
    this.scene.setTimer(left, total);
  }

  private async playEvent(ev: TableEvent): Promise<void> {
    const n = this.st.n;
    const local = toLocal(ev.seat, this.st.me, n);
    switch (ev.kind) {
      case "ace_draw": {
        if (this.ceremonyDone || ev.draw.length === 0) return;
        this.ceremonyDone = true;
        // صحنه باید صندلی‌ها را داشته باشد؛ اگر وضعیت هنوز نرسیده، با نمای سرور می‌سازیم.
        if (!this.seatsLaid) {
          const last = client.table.value;
          if (last) { this.st = applyState(last, this.st); this.gotState = true; }
          this.scene.layoutSeats(this.st); this.seatsLaid = true;
        }
        this.scene.hideHand(true); this.scene.render(this.st);
        const from = toLocal(ev.fromSeat, this.st.me, n);
        await this.scene.animStarterDraw(ev.draw, from, local, this.name(local));
        await this.scene.animShuffle();
        await this.scene.animDealBatch(this.st, local, Math.min(5, this.st.counts[0] || 5));
        this.scene.hideHand(false); this.scene.render(this.st);
        this.refreshPlayable();
        client.aceDraw.value = null;
        break;
      }

      case "trump_called": {
        this.st.trump = ev.suit; this.st.trumpChosen = true;
        this.awaitingTrump.value = false;
        await this.scene.animTrumpAnnounce(ev.suit);
        // «شروع» بعد از حکم می‌آید و نه بعد از پخش: تا حکم معلوم نشده،
        // بازی واقعاً شروع نشده.
        if (!this.startShown) { this.startShown = true; await this.scene.animStartBanner("شروع!"); }
        break;
      }

      case "card_played": {
        if (!ev.card) return;
        const card = ev.card;
        // بریدن پیش از نشستنِ کارت سنجیده می‌شود (بعدش خال عوض شده)
        const cut = cutKindOf(this.st, card);
        // تماشاچی دستی ندارد: صندلیِ ۰ هم مثلِ بقیه فقط یکی از شمارشش کم
        // می‌شود. بی این، بادبزنِ پشت‌وروی آن صندلی سرِ اولین کارت غیب می‌شد.
        if (local === 0 && !this.st.watching) {
          const at = this.st.myHand.findIndex((c) => c.suit === card.suit && c.rank === card.rank);
          if (at >= 0) this.st.myHand.splice(at, 1);
          this.st.counts[0] = this.st.myHand.length;
        } else if (local >= 0 && local < n && (this.st.counts[local] ?? 0) > 0) this.st.counts[local]!--;
        this.scene.render(this.st);
        this.scene.setTurn(this.st);
        await this.scene.animPlayToTrick(local, card);
        if (this.st.trickLeader < 0) { this.st.trickLeader = local; this.st.ledSuit = card.suit; }
        this.st.trick[local] = card;
        this.scene.renderTrick(this.st);
        if (cut.kind !== "none") await this.scene.animCut(this.st, local, cut.kind, cut.order);
        break;
      }

      case "trick_won": {
        if (local < 0 || local >= n) return;
        const u = this.st.seatUnit[local] ?? 0;
        if (u < this.st.tricksWon.length) this.st.tricksWon[u]!++;
        await this.scene.animCollectTrick(this.st, local);
        this.st.trick.fill(null); this.st.trickLeader = -1;
        break;
      }

      case "hand_over": {
        this.st.handOver = true;
        if (ev.kot) await this.scene.animKot(this.st, ev.winnerUnit, ev.points, ev.hakemKot);
        this.proof.value = this.st.watching ? null : await buildProof(this.deal, ev.dealSecret, ev.dealHands, this.st.n,
          this.st.me, (s) => toLocal(s, this.st.me, this.st.n), "ورق‌ها پیش از بازی مهر شد و کسی به آن‌ها دست نزد.");
        this.overlay.value = { kind: "hand", unit: ev.winnerUnit, points: ev.points, kot: ev.kot, hakemKot: ev.hakemKot };
        break;
      }

      case "match_over":
        this.st.matchOver = true;
        this.overlay.value = { kind: "match", unit: ev.winnerUnit };
        break;

      case "seat_bot":
        this.scene.toast(`${this.name(local)} رفت — ربات جایش نشست`);
        break;
      case "seat_reconnect":
        this.scene.toast(`${this.name(local)} برگشت`);
        break;
    }
  }

  // تماشاچی «تو» ندارد: صندلیِ ۰ هم نامِ خودش را دارد.
  name(local: number): string {
    if (local === 0 && !this.st.watching) return "تو";
    return botText(this.st.names[local] || (this.st.watching ? "بازیکن" : "حریف"), this.st.bots[local]);
  }
}

